import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { FormSection } from '@sales-center/components'
import { ISource } from '@user/store/sources/typings'
import { ICampaign } from '@user/store/campaigns/typings'
import { IReferral } from '@sales-center/common'
import {
  entityCampaignId,
  entityLeadSourceId,
} from '@user/components/Settings/EntityFormSettings'
import type { FieldSettingsMap } from '@user/store/entityFormSettings'
import { formatPhoneNumber } from '../../initialValues'
import { TextFieldProps } from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { useData, useEffects } from './hooks'
import { SalesAttributionAlert as Alert } from './styled'
import {
  ReferralFormVariant,
  TidalUpdateReferral,
} from '@user/components/Referrals/components/UpdateReferral/TidalUpdateReferral'
import CampaignSelect from '../../FormFields/CampaignSelect/CampaignSelect'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { LeadSourceSelect } from '../../FormFields/LeadSourceSelect/LeadSourceSelect'
import WarningOutlined from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { ReferralsContextProvider } from '@user/components/Referrals/context'
import { useMapViewAddLeadEnabled } from '@user/store/tenantFeatures/hooks/useMapViewAddLeadEnable'

interface Props {
  leadSources?: ISource[]
  campaigns?: ICampaign[]
  onChangeCampaign?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onChangeReferral: (referral: IReferral) => void
  onChangeSource?: (e: React.ChangeEvent<HTMLInputElement>) => void
  referral: IReferral | null
  salesTeamId: string
  settingsMap: FieldSettingsMap
  textFieldProps?: TextFieldProps
  spacing?: number
}

export function MarketingAttribution(props: Props) {
  const {
    leadSources = [],
    campaigns = [],
    onChangeReferral,
    referral,
    salesTeamId,
    settingsMap,
    spacing = 2,
  } = props
  const tidalThemeActive = useTidalThemeActive()

  const {
    campaignDisabled,
    setCampaignDisabled,
    callTrackingActivity,
    leadSourceId,
    showCampaign,
    requiredCampaignId,
    showLeadSource,
    requiredLeadSourceId,
    showReferral,
    requiredReferral,
    errors,
    touched,
  } = useData({ settingsMap })

  useEffects({
    leadSources,
    campaigns,
    setCampaignDisabled,
    showCampaign,
    requiredCampaignId,
    showLeadSource,
    requiredLeadSourceId,
  })

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const theme = useTheme()
  const mapViewAddLeadEnabled = useMapViewAddLeadEnabled()

  return (
    <FormSection
      label={'Marketing Attribution'}
      spacing={spacing}
      paddingBottom={4}
      maxWidth="660px"
    >
      {callTrackingActivity?.phone && (
        <Box
          sx={{
            paddingTop: theme.spacing(2),
            paddingLeft: theme.spacing(3),
            width: '100%',
          }}
        >
          {campaignDisabled && leadSourceId ? (
            <Alert
              icon={
                <InfoOutlined
                  sx={{ mt: tidalThemeActive ? 1 : undefined }}
                  data-test-id="marketingAttributionInfoIcon"
                />
              }
              severity="info"
            >
              <Typography variant="body2">
                Phone number added above,{' '}
                {formatPhoneNumber(callTrackingActivity.phone)} is associated
                with a marketing campaign. Campaign and Lead Source are provided
                from the tracked call and can be updated after saving the Lead.
              </Typography>
            </Alert>
          ) : (
            <Alert
              icon={
                <WarningOutlined
                  sx={{ mt: tidalThemeActive ? 1 : undefined }}
                />
              }
              severity="warning"
            >
              <Typography variant="body2">
                Phone number added above,{' '}
                {formatPhoneNumber(callTrackingActivity.phone)} is associated
                with a marketing Campaign, but the Lead Source does not match
                any of your existing Lead Sources. Please check the call
                tracking provider for the Lead Source and Campaign.
              </Typography>
            </Alert>
          )}
        </Box>
      )}
      {showCampaign && (
        <Grid item xs={12}>
          <CampaignSelect
            name={entityCampaignId}
            label="Campaign"
            disabled={campaignDisabled}
            leadSourceRequired={requiredLeadSourceId}
            required={requiredCampaignId && !campaignDisabled}
            density={density}
            show={showCampaign}
          />
        </Grid>
      )}
      {showLeadSource && (
        <Grid item xs={12}>
          <LeadSourceSelect
            name={entityLeadSourceId}
            label="Lead Source"
            disabled={campaignDisabled}
            required={requiredLeadSourceId}
            density={density}
            show={showLeadSource}
          />
        </Grid>
      )}
      {showReferral && (
        <Grid item xs={12}>
          <ReferralsContextProvider>
            <TidalUpdateReferral
              onChange={onChangeReferral}
              referral={referral ?? undefined}
              salesTeamId={salesTeamId}
              required={requiredReferral}
              entityErrors={errors}
              touched={touched}
              name="referral"
              variant="gray"
              padding="0"
              formVariant={
                mapViewAddLeadEnabled
                  ? ReferralFormVariant.Inline
                  : ReferralFormVariant.Drawer
              }
            />
          </ReferralsContextProvider>
        </Grid>
      )}
    </FormSection>
  )
}
